import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Space, Spin, Table, Tag, Typography } from 'antd';
import { ClockCircleOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { getUserHistory } from '../../services/userService';

const { Text } = Typography;

function normalizeHistoryResponse(response) {
  if (Array.isArray(response)) {
    return {
      items: response,
      page: 1,
      pageSize: response.length,
      totalRecords: response.length,
    };
  }

  if (response && Array.isArray(response.items)) {
    return {
      items: response.items,
      page: response.page || 1,
      pageSize: response.pageSize || response.items.length || 20,
      totalRecords: response.totalRecords || response.total || response.items.length || 0,
    };
  }

  return { items: [], page: 1, pageSize: 20, totalRecords: 0 };
}

export default function UserHistoryPanel({ title = 'History', compact = false }) {
  const { keycloak } = useKeycloak();
  const [historyRecords, setHistoryRecords] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const showDrawer = () => setOpen(true);
  const onClose = () => setOpen(false);

  const showInDrawer = useCallback((item) => {
    setSelectedItem(item);
    showDrawer();
  }, []);

  const getColor = useCallback((status) => {
    if (!status) return 'default';
    if (status === 'OK - 200') return 'success';
    if (String(status).startsWith('4')) return 'warning';
    if (String(status).startsWith('5')) return 'error';
    return 'default';
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Date & Time',
        dataIndex: 'dateRecorded',
        key: 'dateRecorded',
        render: (date) => (
          <Space>
            <ClockCircleOutlined className='text-gray-400' />
            <Text>{date ? new Date(date).toLocaleString() : '—'}</Text>
          </Space>
        ),
        width: compact ? undefined : '22%',
      },
      {
        title: 'API Request',
        dataIndex: 'requestURI',
        key: 'requestURI',
        render: (uri) => (
          <Space>
            <LinkOutlined className='text-gray-400' />
            <Text className='break-all'>{uri || '—'}</Text>
          </Space>
        ),
        width: compact ? undefined : '48%',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <Tag color={getColor(status)} className='min-w-[90px] text-center'>
            {status || '—'}
          </Tag>
        ),
        width: compact ? undefined : '14%',
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Button onClick={() => showInDrawer(record)} type='link' icon={<InfoCircleOutlined />}>
            Details
          </Button>
        ),
        width: compact ? undefined : '16%',
      },
    ],
    [compact, getColor, showInDrawer],
  );

  const fetchHistory = useCallback(
    async (page = 1, pageSize = 20) => {
      if (!keycloak?.token || !keycloak?.subject) return;
      setLoading(true);
      try {
        const response = await getUserHistory(keycloak.token, keycloak.subject, page, pageSize);
        const normalized = normalizeHistoryResponse(response);
        setHistoryRecords(normalized.items);
        setPagination({
          current: normalized.page,
          pageSize: normalized.pageSize,
          total: normalized.totalRecords,
        });
      } catch (error) {
        console.error('Error fetching history:', error);
        setHistoryRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [keycloak?.token, keycloak?.subject],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleTableChange = (nextPagination) => {
    fetchHistory(nextPagination.current, nextPagination.pageSize);
  };

  return (
    <>
      <Card
        title={
          <div className='flex items-center justify-between'>
            <Text strong className={compact ? 'text-lg' : 'text-2xl'}>
              {title}
            </Text>
          </div>
        }
        className={compact ? '' : 'shadow-md rounded-lg'}
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={historyRecords}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} records`,
            }}
            onChange={handleTableChange}
            rowKey={(record) => record.dateRecorded || record.requestURI || JSON.stringify(record)}
            className='overflow-x-auto'
            scroll={{ x: true }}
            size={compact ? 'small' : 'middle'}
          />
        </Spin>
      </Card>

      <Drawer
        title={
          selectedItem && (
            <div className='space-y-3'>
              <Text strong className='text-lg block'>
                Request Details
              </Text>
              <div className='space-y-2'>
                <div className='text-sm text-gray-600 break-all'>{selectedItem.requestURI}</div>
                <Space size='middle'>
                  <Tag color={getColor(selectedItem.status)} className='px-3 py-1'>
                    {selectedItem.status}
                  </Tag>
                  <Text type='secondary' className='text-sm'>
                    {selectedItem.dateRecorded ? new Date(selectedItem.dateRecorded).toLocaleString() : '—'}
                  </Text>
                </Space>
              </div>
            </div>
          )
        }
        placement='right'
        closable
        onClose={onClose}
        open={open}
        className='shadow-lg'
      >
        {selectedItem && (
          <Card className='border-none shadow-none'>
            <div className='space-y-2'>
              <Text strong className='text-sm text-gray-700'>
                Response Data
              </Text>
              <pre className='mt-2 p-4 bg-gray-50 rounded-lg overflow-auto text-sm font-mono'>
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(selectedItem.historyData), null, 2);
                  } catch {
                    return String(selectedItem.historyData || '—');
                  }
                })()}
              </pre>
            </div>
          </Card>
        )}
      </Drawer>
    </>
  );
}

